import { urlFor } from '../sanity/image-proxy';
import type { HelpArticle, Post } from '../sanity/queries';

export interface OpenGraphMetadata {
	title: string;
	description: string;
	image: string;
	url: string;
	type: 'article';
	author?: string;
	publishedTime?: string;
	modifiedTime?: string;
	siteName: string;
}

export function generateBlogPostMetadata(post: Post, baseUrl: string): OpenGraphMetadata {
	const title = post.title || 'Blog Post';

	// Generate description from excerpt or post body
	let description = post.excerpt;
	if (!description && post.body) {
		// Extract text from the first few blocks of the body
		const textBlocks = post.body
			.filter((block: any) => block._type === 'block' && block.style === 'normal')
			.slice(0, 2) // Take first 2 paragraphs
			.map((block: any) => block.children?.map((child: any) => child.text || '').join('') || '')
			.join(' ')
			.trim();

		// Limit to ~160 characters for meta description
		description = textBlocks.length > 160 ? textBlocks.substring(0, 157) + '...' : textBlocks;
	}

	// Fallback if still no description
	if (!description) {
		description = 'Read this blog post on Retain AI';
	}

	const image = post.mainImage
		? urlFor(post.mainImage).width(1200).height(630).url()
		: `${baseUrl}/og-image.png`;
	const url = post.slug?.current ? `${baseUrl}/blog/post/${post.slug.current}` : `${baseUrl}/blog`;

	return {
		title,
		description,
		image,
		url,
		type: 'article',
		author: post.author?.name,
		publishedTime: post._createdAt,
		modifiedTime: post._createdAt, // Using _createdAt as modified time for now
		siteName: 'Retain AI'
	};
}

export function generateHelpArticleMetadata(
	article: HelpArticle,
	baseUrl: string
): OpenGraphMetadata {
	const title = article.title || 'Help Article';

	// Generate description from excerpt or article body
	let description = article.excerpt;
	if (!description && article.body) {
		// Extract text from the first few blocks of the body
		const textBlocks = article.body
			.filter((block: any) => block._type === 'block' && block.style === 'normal')
			.slice(0, 2) // Take first 2 paragraphs
			.map((block: any) => block.children?.map((child: any) => child.text || '').join('') || '')
			.join(' ')
			.trim();

		// Limit to ~160 characters for meta description
		description = textBlocks.length > 160 ? textBlocks.substring(0, 157) + '...' : textBlocks;
	}

	// Fallback if still no description
	if (!description) {
		description = 'Get help with Retain AI';
	}

	const image = article.mainImage
		? urlFor(article.mainImage).width(1200).height(630).url()
		: `${baseUrl}/og-image.png`;
	const url = article.slug?.current
		? `${baseUrl}/help/article/${article.slug.current}`
		: `${baseUrl}/help`;

	return {
		title,
		description,
		image,
		url,
		type: 'article',
		publishedTime: article.publishedAt || article._createdAt,
		modifiedTime: article.publishedAt || article._createdAt,
		siteName: 'Retain AI'
	};
}

export function generateMetaTags(metadata: OpenGraphMetadata): Record<string, string> {
	const tags: Record<string, string> = {
		// Primary Meta Tags
		title: metadata.title,
		description: metadata.description,

		// Open Graph / Facebook
		'og:type': metadata.type,
		'og:url': metadata.url,
		'og:title': metadata.title,
		'og:description': metadata.description,
		'og:image': metadata.image,
		'og:image:width': '1200',
		'og:image:height': '630',
		'og:site_name': metadata.siteName,

		// Twitter
		'twitter:card': 'summary_large_image',
		'twitter:url': metadata.url,
		'twitter:title': metadata.title,
		'twitter:description': metadata.description,
		'twitter:image': metadata.image
	};

	// Add optional fields if they exist
	if (metadata.author) {
		tags['og:author'] = metadata.author;
		tags['article:author'] = metadata.author;
	}

	if (metadata.publishedTime) {
		tags['og:published_time'] = metadata.publishedTime;
		tags['article:published_time'] = metadata.publishedTime;
	}

	if (metadata.modifiedTime) {
		tags['og:modified_time'] = metadata.modifiedTime;
		tags['article:modified_time'] = metadata.modifiedTime;
	}

	return tags;
}

// Metadata for listing pages
export function generateBlogListingMetadata(baseUrl: string): Record<string, string> {
	return {
		title: 'Blog - Retain AI',
		description:
			'Read the latest insights, tips, and updates from the Retain AI team. Discover productivity strategies and learn how to make the most of your second brain.',
		'og:type': 'website',
		'og:url': `${baseUrl}/blog`,
		'og:title': 'Blog - Retain AI',
		'og:description':
			'Read the latest insights, tips, and updates from the Retain AI team. Discover productivity strategies and learn how to make the most of your second brain.',
		'og:image': `${baseUrl}/og-image.png`,
		'og:image:width': '1200',
		'og:image:height': '630',
		'og:site_name': 'Retain AI',
		'twitter:card': 'summary_large_image',
		'twitter:url': `${baseUrl}/blog`,
		'twitter:title': 'Blog - Retain AI',
		'twitter:description':
			'Read the latest insights, tips, and updates from the Retain AI team. Discover productivity strategies and learn how to make the most of your second brain.',
		'twitter:image': `${baseUrl}/og-image.png`
	};
}

export function generateHelpListingMetadata(baseUrl: string): Record<string, string> {
	return {
		title: 'Help Center - Retain AI',
		description:
			'Get help with Retain AI. Find answers to common questions, tutorials, and guides to help you make the most of your productivity app.',
		'og:type': 'website',
		'og:url': `${baseUrl}/help`,
		'og:title': 'Help Center - Retain AI',
		'og:description':
			'Get help with Retain AI. Find answers to common questions, tutorials, and guides to help you make the most of your productivity app.',
		'og:image': `${baseUrl}/og-image.png`,
		'og:image:width': '1200',
		'og:image:height': '630',
		'og:site_name': 'Retain AI',
		'twitter:card': 'summary_large_image',
		'twitter:url': `${baseUrl}/help`,
		'twitter:title': 'Help Center - Retain AI',
		'twitter:description':
			'Get help with Retain AI. Find answers to common questions, tutorials, and guides to help you make the most of your productivity app.',
		'twitter:image': `${baseUrl}/og-image.png`
	};
}
